import { tierName } from '@h2h/shared';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../../components/ui/bits';
import { Placeholder } from '../../components/ui/Placeholder';
import { Swords, UserPlus, Users } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useOpenProfile } from '../../hooks/useOpenProfile';
import type { FriendSeasonRow } from '../../hooks/useQueries';
import { cap, formatCount, initials } from '../../lib/format';
import { cn } from '../../lib/cn';

function tierChip(tier: number): string {
  switch (tier) {
    case 5:
      return 'bg-[#C9B8F0] text-[#3A2370]';
    case 4:
      return 'bg-[#BFE3FF] text-[#1E4E7A]';
    case 3:
      return 'bg-gradient-to-br from-[#E6ECF5] to-[#9FB2CC] text-[#3C4C66]';
    case 2:
      return 'bg-gradient-to-br from-gold-top to-gold-bottom text-gold-ink';
    case 1:
      return 'bg-fill2 text-[#6E6E73]';
    default:
      return 'bg-[#E8C8A8] text-[#5A3410]';
  }
}

export function FriendsBoard({
  rows,
  leagueName,
  canChallenge,
  onInvite
}: {
  rows: FriendSeasonRow[];
  leagueName: string;
  canChallenge: boolean;
  onInvite: () => void;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const openProfile = useOpenProfile();
  const friendsJoined = rows.filter((r) => !r.isYou).length;

  if (friendsJoined === 0) {
    return (
      <>
        <Placeholder
          Icon={Users}
          title="No friends here yet"
          body={`Invite friends with their @username and they’ll show up on your ${leagueName} board.`}
        />
        <button
          type="button"
          onClick={onInvite}
          className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-[1.5px] border-night bg-card py-[15px]"
        >
          <UserPlus width={18} height={18} strokeWidth={2} className="text-ink" />
          <span className="text-[15px] font-extrabold tracking-snug text-ink">Add a friend</span>
        </button>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {rows.map((row, i) => {
        return (
          <div
            key={row.userId}
            className={cn(
              'flex items-center gap-3 rounded-2xl p-[10px]',
              row.isYou && 'bg-night px-[11px] py-[13px] shadow-[0_12px_26px_-12px_rgba(0,0,0,0.5)]'
            )}
          >
            <span
              className={cn(
                'num w-[18px] text-center text-[14px] font-extrabold',
                row.isYou ? 'text-accent' : 'text-ink'
              )}
            >
              {i + 1}
            </span>
            <button type="button" onClick={() => openProfile(row.userId, row.isYou)} className="tap flex min-w-0 flex-1 items-center gap-3 text-left">
              {row.isYou ? (
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-[12px] font-extrabold text-ink">
                  {initials(row.displayName)}
                </div>
              ) : (
                <Avatar name={row.displayName} tone={row.avatarTone} size={34} />
              )}
              <div className="min-w-0 flex-1">
                <div className={cn('truncate text-[14px] font-bold tracking-snug', row.isYou ? 'text-white' : 'text-ink')}>
                  {row.isYou ? 'You' : row.displayName}
                </div>
                <div className="mt-[2px]">
                  <span
                    className={cn(
                      'inline-block rounded-[5px] px-[6px] py-[2px] text-[9px] font-extrabold tracking-wide',
                      tierChip(row.tier)
                    )}
                  >
                    {cap(tierName(row.tier)).toUpperCase()}
                  </span>
                </div>
              </div>
            </button>
            <span className={cn('num text-[16px] font-extrabold', row.isYou ? 'text-accent' : 'text-ink')}>
              {formatCount(row.seasonPoints)}
            </span>
            {!row.isYou && canChallenge && (
              <button
                type="button"
                aria-label={`Challenge ${row.displayName}`}
                onClick={() => {
                  impact('medium');
                  navigate(`/challenge/${row.userId}`);
                }}
                className="tap grid h-8 w-8 place-items-center rounded-full bg-fill2 text-ink transition-colors hover:bg-fill active:scale-95"
              >
                <Swords width={15} height={15} strokeWidth={2} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
